import axios, { type AxiosResponse } from 'axios'
import { notify } from './Notifications'

export const api = new class {
  bearerToken: string = ''

  axios = axios.create({
    withCredentials: true,
    headers: {
    },
  })

  getBackendUrl() {
    let r = (import.meta.server)
      ? useRuntimeConfig().public.SERVER_URL_INTERNAL
      : useRuntimeConfig().public.SERVER_URL

    if (import.meta.server){
      this.axios.defaults.headers.common['X-Forwarded-For'] = useRequestHeaders()?.['x-real-ip'] ?? useRequestHeaders()?.['x-forwarded-for']
      this.axios.defaults.headers.common['X-Real-Ip'] = useRequestHeaders()?.['x-real-ip'] ?? useRequestHeaders()?.['x-forwarded-for']
    }

    if (!r) {
      throw new Error('SERVER_URL not defined in runtime config')
    }

    // if last is not /
    if (r[r.length - 1] !== '/') {
      r += '/'
    }

    return r
  }

  cleanseUrl(url: string) {
    if (url[0] === '/') {
      url = url.substring(1)

    }else{
      // if first 3 are not api
      if (url.substring(0, 3) !== 'api') {
        url = 'api/' + url
      }
    }


    return url
  }

  setBearer(token: string|null) {

    if(!token){
      this.bearerToken = ''
      localStorage.removeItem('bearerToken')
      sessionStorage.removeItem('bearerToken')
      delete this.axios.defaults.headers.common['Authorization']
      return
    }else{
      this.bearerToken = token
      localStorage.setItem('bearerToken', token)

      // set default header for axios
      this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
    }
  }

  postware(response: any) {
    return response
      .then((r: AxiosResponse) => {
        if(devCollectLogs.value){
          devAppendLog(r,'response')
        }

        return r
      })
      .catch((e: any) => {
        if(devCollectLogs.value){
          devAppendLog(e,'response-thrown')
        }

        throw e;
      })
  }

  post<T>(url: string, data: any = undefined, headers: any = undefined) : Promise<AxiosResponse<T>> {

    devAppendLog({
      url: this.getBackendUrl() + this.cleanseUrl(url),
      data: data,
      headers: headers,
    }, 'axios-post')

    return this.postware(this.axios.post(this.getBackendUrl() + this.cleanseUrl(url), data, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': headers?.['Content-Type'] ?? 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  // new helper to recursively serialize nested objects into query parameters
  private serializeParams(params: any, prefix = ''): string {
    const queryParts: string[] = []
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key]
        const newKey = prefix ? `${prefix}[${encodeURIComponent(key)}]` : encodeURIComponent(key)
        if (value !== null && typeof value === 'object') {
          queryParts.push(this.serializeParams(value, newKey))
        } else {
          queryParts.push(`${newKey}=${encodeURIComponent(value)}`)
        }
      }
    }
    return queryParts.join('&')
  }

  get<T>(url: string, data: any = undefined) : Promise<AxiosResponse<T>> {
    // Replace URLSearchParams with serializeParams for handling nested objects
    let urlParams = data ? this.serializeParams(data) : ''

    if (urlParams.length > 0) {
      urlParams = '?' + urlParams
    }

    devAppendLog({
      url: this.getBackendUrl() + this.cleanseUrl(url),
      data: data,
    }, 'axios-get')

    return this.postware(this.axios.get(this.getBackendUrl() + this.cleanseUrl(url) + urlParams, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  put<T>(url: string, data: any = undefined) : Promise<AxiosResponse<T>> {

    devAppendLog({
      url: this.getBackendUrl() + this.cleanseUrl(url),
      data: data,
    }, 'axios-put')

    return this.postware(this.axios.put(this.getBackendUrl() + this.cleanseUrl(url), data, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  delete<T>(url: string, headers: any = undefined) : Promise<AxiosResponse<T>> {

    devAppendLog({
      url: this.getBackendUrl() + this.cleanseUrl(url),
    }, 'axios-delete')

    return this.postware(this.axios.delete(this.getBackendUrl() + this.cleanseUrl(url), {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': headers?.['Content-Type'] ?? 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  async xsrf() {
    console.log('Getting xsrf')
    return this.get('/sanctum/csrf-cookie')
  }

  parseError(e: any) {
    if(!(e?.response?.data?.message ?? e?.response?.message ?? e?.message)) throw e

    notify({
      text: e?.response?.data?.message ?? e?.response?.message ?? e?.message ?? 'An unknown error occurred',
      type: 'error',
      errors: e?.response?.data?.errors,
      timeout: 3000,
    })

    throw e
  }
}
